import {
  QUESTIONNAIRE_NOTE_MAX,
  QUESTIONNAIRE_OTHER_MAX,
  isPlausibleBirthDate,
  type ContactMethod,
  type HeardFrom,
  type QuestionnaireDefinition,
  type QuestionnaireFormRequest,
} from '@360face/domain';

/**
 * Client form being filled in on the kiosk (v2, owner 15.10.2026). Lives only in component
 * state: gone after submit, "cancel" or the idle reset (privacy — nothing in browser storage).
 * Four pages: about you · health · aesthetic procedures · confirm & sign.
 */
export const FORM_PAGES = ['about', 'health', 'aesthetic', 'sign'] as const;
export type FormPage = (typeof FORM_PAGES)[number];

/** v4 (simple, owner 19.10.2026): no aesthetic page — the list holds the time windows. */
export function formPages(def: Pick<QuestionnaireDefinition, 'simple'>): readonly FormPage[] {
  return def.simple ? ['about', 'health', 'sign'] : FORM_PAGES;
}

/** v4: the one confirmation ("none applies; I'll tell the therapist") answers health and aesthetic. */
export function setConfirmed(d: FormDraft, on: boolean): FormDraft {
  return on
    ? { ...d, conditionsNone: true, conditionIds: [], conditionNote: '', aestheticHad: false, aestheticDetail: '' }
    : { ...d, conditionsNone: undefined, aestheticHad: undefined };
}

export interface FormDraft {
  fullName: string;
  /** Digits typed as DDMMYYYY (display adds the slashes). */
  birthDigits: string;
  contact?: ContactMethod;
  pressure?: number;
  heardFrom?: HeardFrom;
  similarBefore?: boolean;
  /** undefined = not answered; true = "none of these"; false = "something applies". */
  conditionsNone?: boolean;
  conditionIds: string[];
  conditionNote: string;
  aestheticHad?: boolean;
  aestheticDetail: string;
  otherNote: string;
  consent: boolean;
  /** PNG data URL of the finger signature. */
  signature?: string;
}

export function emptyForm(fullName = ''): FormDraft {
  return {
    fullName,
    birthDigits: '',
    conditionIds: [],
    conditionNote: '',
    aestheticDetail: '',
    otherNote: '',
    consent: false,
  };
}

/** "DDMMYYYY" digits → "DD/MM/YYYY" as typed so far. */
export function formatBirth(digits: string): string {
  const d = digits.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/** "DDMMYYYY" → "YYYY-MM-DD" (only when complete). */
export function birthIso(digits: string): string | undefined {
  const d = digits.replace(/\D/g, '');
  if (d.length !== 8) return undefined;
  return `${d.slice(4)}-${d.slice(2, 4)}-${d.slice(0, 2)}`;
}

/** "None of these" clears the ticks and the note. */
export function setConditionsNone(d: FormDraft, none: boolean): FormDraft {
  return none ? { ...d, conditionsNone: true, conditionIds: [], conditionNote: '' } : { ...d, conditionsNone: false };
}

export function toggleCondition(d: FormDraft, id: string): FormDraft {
  if (d.conditionsNone !== false) return d;
  const has = d.conditionIds.includes(id);
  return { ...d, conditionIds: has ? d.conditionIds.filter((x) => x !== id) : [...d.conditionIds, id] };
}

/** "No" clears the detail typed after an earlier "yes". */
export function setAesthetic(d: FormDraft, had: boolean): FormDraft {
  return { ...d, aestheticHad: had, aestheticDetail: had ? d.aestheticDetail : '' };
}

/** Fields still blocking each page (same rules as the API schema). */
export function pageMissing(page: FormPage, d: FormDraft, today: string): string[] {
  const m: string[] = [];
  if (page === 'about') {
    if (d.fullName.trim().length < 2) m.push('fullName');
    const iso = birthIso(d.birthDigits);
    if (!iso || !isPlausibleBirthDate(iso, today)) m.push('birthDate');
    if (!d.contact) m.push('contact');
    if (!d.pressure) m.push('pressure');
    if (d.similarBefore === undefined) m.push('similarBefore');
    if (!d.heardFrom) m.push('heardFrom');
  }
  if (page === 'health') {
    if (d.conditionsNone === undefined) m.push('conditions');
    if (d.conditionsNone === false && d.conditionIds.length === 0 && !d.conditionNote.trim()) m.push('conditionIds');
  }
  if (page === 'aesthetic') {
    if (d.aestheticHad === undefined) m.push('aesthetic');
    if (d.aestheticHad && d.aestheticDetail.trim().length < 2) m.push('aestheticDetail');
  }
  if (page === 'sign') {
    if (!d.consent) m.push('consent');
    if (!d.signature) m.push('signature');
  }
  return m;
}

export function formReady(d: FormDraft, today: string): boolean {
  return FORM_PAGES.every((p) => pageMissing(p, d, today).length === 0);
}

export function toFormRequest(def: QuestionnaireDefinition, d: FormDraft, today: string): QuestionnaireFormRequest {
  if (!formReady(d, today)) throw new Error('questionnaire form is not complete');
  const note = d.conditionNote.trim().slice(0, QUESTIONNAIRE_NOTE_MAX);
  const other = d.otherNote.trim().slice(0, QUESTIONNAIRE_OTHER_MAX);
  return {
    version: def.version,
    fullName: d.fullName.trim(),
    birthDate: birthIso(d.birthDigits)!,
    contact: d.contact!,
    pressure: d.pressure!,
    heardFrom: d.heardFrom!,
    similarBefore: d.similarBefore!,
    conditions: d.conditionsNone
      ? { none: true, ids: [] }
      : {
          none: false,
          ids: def.conditions.map((c) => c.id).filter((id) => d.conditionIds.includes(id)),
          ...(note ? { note } : {}),
        },
    aesthetic: d.aestheticHad
      ? { had: true, detail: d.aestheticDetail.trim().slice(0, QUESTIONNAIRE_NOTE_MAX) }
      : { had: false },
    ...(other ? { otherNote: other } : {}),
    consent: true,
    signature: d.signature!,
  };
}
